import React, {
  FunctionComponent,
  useCallback,
  useState,
  KeyboardEvent,
  useRef,
  MouseEventHandler,
} from 'react'
import {
  Direction,
  QuickKey,
  QuickKeyFolder,
  QuickKeyKey,
} from '../../quick-keys/qk-types'
import { QuickKeyNodeType } from '../../quick-keys/qk.constants'
import { navigate } from '../../quick-keys/qk-navigation'
import { useAddProductToSale } from '../AddProductToSale/useAddProductToSale'
import { getProductDetails } from '../../quick-keys/qk-layout'
import { useQuickKeysLayout } from './useQuickKeysLayout'
import { QKRoot } from './nodes/QKRoot'
import { NoQuickKeys } from './NoQuickKeys'

const KEY_MAP: Record<string, Direction> = {
  ArrowLeft: 'LEFT',
  ArrowRight: 'RIGHT',
  ArrowUp: 'UP',
  ArrowDown: 'DOWN',
}

const getFirstChild = (node: QuickKey) => {
  if (!('children' in node && node.children.length)) {
    return null
  }

  const firstChild = node.children[0]
  if (
    firstChild.type === QuickKeyNodeType.FOLDER ||
    firstChild.type === QuickKeyNodeType.QUICK_KEY
  ) {
    return firstChild
  }

  return getFirstChild(firstChild)
}

export const QuickKeys: FunctionComponent = () => {
  const [currentRoot, setCurrentRoot] = useState<QuickKeyFolder | null>(null)
  const [focussed, setFocussed] = useState<QuickKeyFolder | QuickKeyKey | null>(
    null
  )
  const container = useRef<HTMLDivElement>(null)
  const layout = useQuickKeysLayout()
  const { addProductToSale } = useAddProductToSale()

  const onFocus = useCallback(() => {
    const root = currentRoot || layout
    if (!focussed && root) {
      const firstChild = getFirstChild(root)
      setFocussed(firstChild || null)
    }
  }, [focussed, currentRoot, layout])

  const selectQuickKey = useCallback(
    (quickKey: QuickKey | null) => {
      switch (quickKey?.type) {
        case QuickKeyNodeType.FOLDER:
          setCurrentRoot(quickKey)
          setFocussed(getFirstChild(quickKey) || null)
          break
        case QuickKeyNodeType.QUICK_KEY:
          addProductToSale(getProductDetails(quickKey))
          break
        default:
          setCurrentRoot(null)
      }
    },
    [addProductToSale]
  )

  const keyDownHandler = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const handleDirectionKey = (direction: Direction) => {
        if (!layout) {
          return
        }
        e.preventDefault()
        const pageIndex =
          focussed && 'parentNode' in focussed
            ? focussed.parentNode.position
            : 0
        const fromPosition = {
          position: focussed?.node.position || 0,
          pageIndex,
        }
        const result = navigate(currentRoot || layout, {
          fromPosition,
          direction,
        })
        if (
          result.to?.type === QuickKeyNodeType.FOLDER ||
          result.to?.type === QuickKeyNodeType.QUICK_KEY
        ) {
          setFocussed(result.to)
        } else {
          setFocussed(null)
        }
      }

      switch (e.key) {
        case 'Enter':
          e.preventDefault()
          selectQuickKey(focussed)
          break
        case 'Escape':
          if (!currentRoot) {
            return
          }
          e.preventDefault()
          setFocussed(currentRoot)
          setCurrentRoot(null)
          break
        case 'ArrowLeft':
          handleDirectionKey(KEY_MAP.ArrowLeft)
          break
        case 'ArrowRight':
          handleDirectionKey(KEY_MAP.ArrowRight)
          break
        case 'ArrowUp':
          handleDirectionKey(KEY_MAP.ArrowUp)
          break
        case 'ArrowDown':
          handleDirectionKey(KEY_MAP.ArrowDown)
          break
      }
    },
    [currentRoot, focussed, layout, selectQuickKey]
  )

  const handleGetFocus: MouseEventHandler<HTMLDivElement> = useCallback(
    e => {
      if (
        container.current &&
        !container.current.contains(document.activeElement)
      ) {
        const currentFocus = focussed
        setFocussed(null)
        setTimeout(() => {
          setFocussed(currentFocus)
        })
      }
    },
    [focussed]
  )

  if (!layout) {
    return <NoQuickKeys />
  }

  return (
    <div
      data-testid="quick-keys-container"
      className="wr-quick-keys wr-workspace-module wr-workspace-module--fit-container"
      onFocus={onFocus}
      onKeyDown={keyDownHandler}
      ref={container}
      onClick={handleGetFocus}
      tabIndex={focussed ? undefined : 0}
    >
      <QKRoot
        quickKey={currentRoot || layout}
        setRoot={setCurrentRoot}
        setFocus={setFocussed}
        focussed={focussed}
        selectQuickKey={selectQuickKey}
      />
    </div>
  )
}
